import clsx from 'clsx';
import React, { useContext } from 'react';
import {
  OverlayTriggerStateContext,
  Dialog as ReactAriaDialog,
  DialogProps as ReactAriaDialogProps,
} from 'react-aria-components';
import styles from './DialogBase.module.scss';

export type DialogBaseProps = {
  /** The description of the dialog to identify the element to assistive technology. It must be provided if you don't use `ModalDialogHeader` and `ModalDialogRoot` does not contain a visible heading. */
  'aria-label'?: ReactAriaDialogProps['aria-label'];
  /** The ID of an element that labels the modal. It must be provided if you use a custom heading instead of using `ModalDialogHeader`. */
  'aria-labelledby'?: ReactAriaDialogProps['aria-labelledby'];
  /** The children to render inside the component. */
  children: ReactAriaDialogProps['children'];
  /** Optional property for overriding styles with CSS-in-JS. */
  className?: string;
  /** Optional property for overriding styles with CSS-in-JS. */
  style?: React.CSSProperties;
};

/**
 * Base wrapper around React Aria's `Dialog` component. Customizations to the dialog (i.e. the white box) can theoretically be applied here.
 * @private
 */
export const DialogBase = ({
  children,
  className,
  style,
  ...props
}: DialogBaseProps) => {
  const state = useContext(OverlayTriggerStateContext);
  const close = React.useCallback(() => state?.close(), [state]);

  return (
    <ReactAriaDialog
      aria-label={props['aria-label']}
      aria-labelledby={props['aria-labelledby']}
      className={clsx(styles.dialog, className)}
      data-zds
      style={style}
      {...props}
    >
      {typeof children === 'function' ? children({ close }) : children}
    </ReactAriaDialog>
  );
};
